import {
  AdditionalCartDetailsContent,
  AdditionalCartDetailsTitle,
  AdditionalCartDetailsWrapper,
  BackWrapper,
  BottomWrapper,
  CardsWrapper,
  Container,
  IconWrapper,
  PricingLabel,
  PricingRow,
  PricingRowTotal,
  PricingValue,
  PricingValueTotal,
  PricingWrapper,
  ProductsTitle,
  ProductsWrapper,
  RetryWrapper,
  StatusLabel,
  StatusWrapper,
  SubTitle,
  Title,
  TitleWrapper,
} from './BuyerSingleOrderPage.styled';
import { Repeat } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import BackButton from 'components/BackButton/BackButton';
import { useTranslation } from 'react-i18next';
import { RoutesConfig } from 'navigation/routes';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetSingleOrder,
  useRetryOrder,
} from 'pages/SupplierOrdersPage/hooks';
import {
  canRetryOrder,
  formatOrderStatus,
  getOrderColor,
} from 'utils/orderUtils';
import OrderStatusNote from 'components/OrderStatusNote/OrderStatusNote';
import SimpleWideCard from 'components/SimpleWideCard/SimpleWideCard';
import { ICartItem } from 'types/CartItem.types';
import ShoppingCartCard from 'pages/ShoppingCartPage/ShoppingCartCard/ShoppingCartCard';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import moment from 'moment';
import { getCountryNameByCode } from 'utils/countryUtils';
import { AppMode } from 'store/Common/reducers/common';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { useState } from 'react';
import { ConfirmModal } from 'components/Modals/ConfirmModal/ConfirmModal';

const BuyerSingleOrderPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const { data, isFetching: isFetchingOrder } = useGetSingleOrder(
    typeof id === 'string' ? id : '',
    appMode
  );

  const { mutate: retryOrder, isLoading: isRetrying } = useRetryOrder();

  const formatMoney = useFormatMoney(data?.currency);

  const supplierAddress = data?.company_seller.address;

  const supplierDetailsCardLeftData = {
    Name: data?.company_seller.name,
    ...(supplierAddress && {
      Address: `${supplierAddress.street || ''} ${
        supplierAddress.house_number || ''
      } ${supplierAddress.city || ''}`.trim(),
      'Zip code': supplierAddress.zip_code,
    }),
  };

  const supplierDetailsCardRightData = {
    'E-mail': data?.company_seller.email,
    'Phone number': data?.company_seller.phone_number,
  };

  // Determine shipping type and data
  const shippingTypeLabel = t('Shipping Type');
  let shippingLeftData = {};
  let shippingRightData = {};

  if (data?.shipping_address?.is_default) {
    // Scenario 1: Default Address
    shippingRightData = {
      [shippingTypeLabel]: t('Default address'),
    };
  } else if (data?.shipping_address?.is_pickup) {
    // Scenario 2: Pickup
    shippingRightData = {
      [shippingTypeLabel]: t('Pick up'),
    };
  } else if (data?.shipping_address?.code) {
    // Scenario 3: Alternative Address
    const fullAddress = `${data?.shipping_address.address.street} ${
      data?.shipping_address.address.house_number || ''
    } ${data?.shipping_address.address.zip_code} ${
      data?.shipping_address.address.city
    } ${getCountryNameByCode(data?.shipping_address.address.country)}`;

    shippingLeftData = {
      [shippingTypeLabel]: t('Alternative address'),
      'Ship-to Code': data?.shipping_address.code,
      'Ship-to Contact': data?.shipping_address.contact_person,
    };
    shippingRightData = {
      'Ship-to Address': fullAddress,
    };
  } else if (data?.shipping_address) {
    // Scenario 4: Consumer Delivery
    const fullAddress = `${data?.shipping_address.address.street} ${
      data?.shipping_address.address.house_number || ''
    } ${data?.shipping_address.address.zip_code} ${
      data?.shipping_address.address.city
    } ${getCountryNameByCode(data?.shipping_address.address.country)}`;

    shippingLeftData = {
      [shippingTypeLabel]: t('Consumer Delivery'),
      'Consumer Name': data?.shipping_address.contact_person,
      Address: fullAddress,
    };
    shippingRightData = {
      'E-mail address': data?.shipping_address.email,
      'Phone number': data?.shipping_address.phone_number,
    };
  }

  const orderDetailsCardLeftData = {
    'Logyx Order ID': data?.number,
    ...(data?.internal_number && {
      'Supplier Order Number': data?.internal_number,
    }),
    ...(data?.requested_delivery_date && {
      'Requested Delivery Date': moment(data?.requested_delivery_date).format(
        'DD.MM.YYYY'
      ),
    }),
    ...shippingLeftData,
  };

  const orderDetailsCardRightData = {
    ...(data?.confirmed_delivery_date && {
      'Confirmed Delivery Date': moment(data?.confirmed_delivery_date).format(
        'DD.MM.YYYY'
      ),
    }),
    'Purchase Amount': formatMoney(data?.total_net_price),
    'Supplier Order Reference': data?.reference,
    ...shippingRightData,
  };

  return (
    <Container>
      <BackWrapper>
        <BackButton
          destination={t('Orders')}
          onClick={() =>
            navigate(RoutesConfig.BuyerOrders.fullPath, { replace: true })
          }
        />
      </BackWrapper>
      <TitleWrapper>
        <Title>{data?.internal_number}</Title>
        {!data?.internal_number && (
          <SubTitle>
            {t('Logyx Order ID')}: ({data?.number})
          </SubTitle>
        )}
        <StatusWrapper>
          <StatusLabel $color={getOrderColor(data)} data-testid="order-status">
            {t(formatOrderStatus(data))}
          </StatusLabel>
        </StatusWrapper>
        {canRetryOrder(data) && (
          <RetryWrapper
            $disabled={isRetrying}
            data-testid="order-retry"
            onClick={isRetrying ? undefined : () => setIsConfirmModalOpen(true)}
          >
            {t('Retry')}
            <IconWrapper>
              <Icon svg={Repeat} color={COLORS.GREEN} size={20} weight="bold" />
            </IconWrapper>
          </RetryWrapper>
        )}
      </TitleWrapper>

      <OrderStatusNote order={data} />

      <CardsWrapper>
        <SimpleWideCard
          cardTitle={t('Supplier details')}
          leftSubtitle={t('GENERAL')}
          rightSubtitle={t('CONTACT')}
          leftData={supplierDetailsCardLeftData}
          rightData={supplierDetailsCardRightData}
          isLoading={isFetchingOrder}
        />
        <SimpleWideCard
          cardTitle={t('Order details')}
          leftSubtitle={''}
          rightSubtitle={''}
          leftData={orderDetailsCardLeftData}
          rightData={orderDetailsCardRightData}
          isLoading={isFetchingOrder}
        />
      </CardsWrapper>

      <ProductsWrapper>
        <ProductsTitle>{t('Products')}</ProductsTitle>
        {data?.lines?.map((item: ICartItem, index: number) => (
          <ShoppingCartCard
            item={item}
            key={`cart-item-${index}`}
            isPreview={true}
            buyerMode={true}
            isGrossVisible={data.is_gross_visible}
            currency={data?.currency}
          />
        ))}

        <BottomWrapper>
          {data?.additional_details && (
            <AdditionalCartDetailsWrapper>
              <AdditionalCartDetailsTitle>
                {t('Additional cart details')}
              </AdditionalCartDetailsTitle>
              <AdditionalCartDetailsContent>
                {data.additional_details}
              </AdditionalCartDetailsContent>
            </AdditionalCartDetailsWrapper>
          )}

          <PricingWrapper>
            {data?.total_gross_price !== data?.total_net_price &&
              data?.is_gross_visible && (
                <PricingRow>
                  <PricingLabel>{t('Subtotal')}:</PricingLabel>
                  <PricingValue>
                    {formatMoney(data?.total_gross_price)}
                  </PricingValue>
                </PricingRow>
              )}
            {data?.total_gross_price !== data?.total_net_price &&
              data?.is_gross_visible && (
                <PricingRow>
                  <PricingLabel>{t('Discount')}:</PricingLabel>
                  <PricingValue>
                    {formatMoney(data?.total_model_discount)}
                  </PricingValue>
                </PricingRow>
              )}
            <PricingRow>
              <PricingLabel>{t('Total excl. VAT')}:</PricingLabel>
              <PricingValue>{formatMoney(data?.total_net_price)}</PricingValue>
            </PricingRow>
            <PricingRow>
              <PricingLabel>
                {`${t('VAT')} (${data?.vat_percentage}%)`}:
              </PricingLabel>
              <PricingValue>{formatMoney(data?.vat)}</PricingValue>
            </PricingRow>
            <PricingRowTotal>
              <PricingLabel>{t('Total incl. VAT')}:</PricingLabel>
              <PricingValueTotal>
                {formatMoney(data?.total_net_price_incl_vat)}
              </PricingValueTotal>
            </PricingRowTotal>
          </PricingWrapper>
        </BottomWrapper>
      </ProductsWrapper>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        title={t('Confirm retry')}
        description={t('Are you sure you want to retry ordering?')}
        onConfirm={() => {
          if (!isRetrying) retryOrder({ id: data?.id });
          setIsConfirmModalOpen(false);
        }}
        onCancel={() => setIsConfirmModalOpen(false)}
      />
    </Container>
  );
};

export default BuyerSingleOrderPage;
